import axios, { RawAxiosRequestHeaders } from 'axios';
import { removeUserAuthInfo, getBaseHeaderParams } from './index';

type DataType = Record<string, any>;

export interface RequestParams<Req = any> {
  /**
   * 请求方式
   * @default post
   */
  method?: 'post' | 'get';
  /**
   * 请求地址
   * @default undefined
   */
  url: string;
  /**
   * 请求参数
   * @default undefined
   */
  data?: Req;
  /**
   * 数据类型
   * @default json
   */
  dataType?: 'json' | 'text';
  /**
   * 服务类型
   * @default trpc
   */
  serviceType?: 'trpc' | 'php';
  /**
   * 鉴权
   * @default 0
   */
  authType?: 1 | 0;
  /**
   * 请求头
   * @default undefined
   */
  headers?: RawAxiosRequestHeaders;
  /**
   * 接口是否需要登录态
   * @defualt false
   */
  auth?: boolean;
}

export interface RequestResult<T = any> {
  code: number;
  data: T;
  message: string;
}

export const request = async <R = any, P = any>(params: RequestParams<P>): Promise<RequestResult<R>> => {
  const { method = 'post', data, dataType = 'json', headers, auth = false } = params;
  let { url } = params;
  if (!url.startsWith('http')) {
    url = process.env.REACT_APP_BASE_URL + url;
  }
  let result: RequestResult<R>;

  try {
    // https://github.com/axios/axios#request-config

    const resp = await axios({
      method,
      url,
      data,
      params: method === 'get' ? data : undefined,
      responseType: dataType,
      headers: { ...getBaseHeaderParams(), ...headers },
    });

    const code = processCode(resp.data);
    const message = processMessage(resp.data);
    const resultData = resp.data.data ?? resp.data;

    result = {
      code: code === undefined || code === 0 ? 0 : code,
      message: message ?? 'ok',
      data: resultData,
    };
  } catch (error: any) {
    if (error.response?.data?.code === -102 || error.response?.data?.code === -105) {
      removeUserAuthInfo();
    }
    result = (
      error.response
        ? {
            code: Number(error.response?.data?.code) || error.response.status,
            message: error.response.statusText,
            data: undefined,
          }
        : {
            code: -1,
            message: error.message,
            data: undefined,
          }
    ) as RequestResult<R>;
  }

  return result;
};

export const processCode = (data: DataType = {}) => data.code ?? data.returnCode ?? data.resultCode ?? data.retCode;
export const processMessage = (data: DataType = {}) => data.message ?? data.returnMessage ?? data.returnMsg ?? data.msg;

// 下载函数
export const download = (url, name, onProgress, onComplete) =>
  new Promise((resolve, reject) => {
    axios(url, {
      method: 'get',
      responseType: 'blob',
      onDownloadProgress: (evt: any) => {
        const progress = Math.floor((evt.loaded / evt.total) * 100);
        onProgress?.(progress);
      },
    })
      .then((res) => {
        const blob = new Blob([res.data]);
        const a = document.createElement('a');
        a.download = name;
        a.href = URL.createObjectURL(blob);
        a.click();
        onComplete?.();
        URL.revokeObjectURL(a.href);
        a.remove();
        resolve(true);
        return true;
      })
      .catch((err) => {
        reject(err);
      });
  });
